import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Star } from 'lucide-react';
import { contentApi } from '../../services/contentApi';
import Button from '../UI/Button';
import { useAuth } from '../../context/AuthContext';
import CourseScheduleInterviews from './CourseScheduleInterviews';
import CourseInterviewUpgrade from './CourseInterviewUpgrade';
import CourseRating, { readCourseRating } from './CourseRating';
import { CourseEnrollment, CourseInterviewPackageConfig } from '../../types/content';

type CourseLearnAsideProps = {
  courseId: number;
  courseSlug: string;
  courseTitle: string;
  interviewPackageCount?: number;
  enrollment?: CourseEnrollment | null;
  interviewPackages?: CourseInterviewPackageConfig | null;
  onEnrollmentChange?: (enrollment: CourseEnrollment) => void;
  averageRating?: number;
  ratingCount?: number;
  onRated?: (average: number, count: number) => void;
};

const CourseLearnAside: React.FC<CourseLearnAsideProps> = ({
  courseId,
  courseSlug,
  courseTitle,
  interviewPackageCount = 0,
  enrollment = null,
  interviewPackages = null,
  onEnrollmentChange,
  averageRating,
  ratingCount,
  onRated,
}) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [ratingNote, setRatingNote] = useState('');
  const [ratingMessage, setRatingMessage] = useState<string | null>(null);
  const [upgradeMessage, setUpgradeMessage] = useState<string | null>(null);
  const [pkgCount, setPkgCount] = useState(interviewPackageCount);

  useEffect(() => {
    setPkgCount(interviewPackageCount);
  }, [interviewPackageCount]);

  const displayStars = hoverRating || rating;

  return (
    <aside className="lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto space-y-4">
      <CourseScheduleInterviews
        courseId={courseId}
        courseSlug={courseSlug}
        courseTitle={courseTitle}
        interviewPackageCount={pkgCount}
      />

      {enrollment?.isPaid && (
        <CourseInterviewUpgrade
          courseId={courseId}
          enrollment={enrollment}
          packages={interviewPackages}
          onUpgraded={(next, message) => {
            setPkgCount(next.interviewPackageCount || 0);
            onEnrollmentChange?.(next);
            setUpgradeMessage(message || 'Review package upgraded.');
          }}
        />
      )}
      {upgradeMessage && <p className="text-xs text-indigo-800 px-1">{upgradeMessage}</p>}

      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Rate this course</p>
        <p className="mt-1">
          <CourseRating
            average={readCourseRating({ averageRating, ratingCount }).average}
            count={readCourseRating({ averageRating, ratingCount }).count}
            className="text-sm text-amber-700"
            showEmpty
          />
        </p>
        {isAuthenticated ? (
          <form
            className="mt-3 space-y-2"
            onSubmit={async (event) => {
              event.preventDefault();
              setRatingMessage(null);
              try {
                const response = await contentApi.rateCourse(courseId, rating, ratingNote);
                const next = readCourseRating(response.data);
                onRated?.(next.average, next.count);
                setRatingMessage(response.data.message || 'Rating saved.');
              } catch (err: unknown) {
                setRatingMessage(err instanceof Error ? err.message : 'Could not save the rating.');
              }
            }}
          >
            <div>
              <p className="text-sm text-gray-700 mb-1">Your rating</p>
              <div
                className="flex items-center gap-0.5"
                role="radiogroup"
                aria-label="Course rating"
                onMouseLeave={() => setHoverRating(0)}
              >
                {[1, 2, 3, 4, 5].map((value) => {
                  const filled = value <= displayStars;
                  return (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={rating === value}
                      aria-label={`${value} star${value === 1 ? '' : 's'}`}
                      className="p-0.5 rounded text-amber-400 hover:text-amber-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
                      onMouseEnter={() => setHoverRating(value)}
                      onFocus={() => setHoverRating(value)}
                      onBlur={() => setHoverRating(0)}
                      onClick={() => setRating(value)}
                    >
                      <Star className={`h-6 w-6 ${filled ? 'fill-current' : ''}`} strokeWidth={1.75} />
                    </button>
                  );
                })}
              </div>
            </div>
            <label className="block text-sm text-gray-700">
              Comment (optional)
              <input className="mt-1 w-full rounded-md border-gray-300" value={ratingNote} onChange={(e) => setRatingNote(e.target.value)} />
            </label>
            <Button type="submit" size="sm" fullWidth>Save rating</Button>
          </form>
        ) : (
          <Link to="/login" state={{ from: location.pathname }} className="mt-3 inline-block text-sm font-semibold text-primary-700">
            Sign in to rate
          </Link>
        )}
        {ratingMessage && <p className="mt-2 text-sm text-gray-600">{ratingMessage}</p>}
      </div>
    </aside>
  );
};

export default CourseLearnAside;
